import React from "react";
import IntegrationConversionSettings from "./IntegrationConversionSettings";
import {IntegrationAdAccountList, IntegrationAdAccountRow} from "./IntegrationAdAccounts";
import {Box, Button, DialogActions, DialogContent, List, StepConnector, TextField, useMediaQuery, useTheme, Step, StepLabel, Stepper, Alert, Link} from "@mui/material";
import {IntegrationDialog as Dialog, IntegrationHeader, IntegrationBackButton} from "./IntegrationDesign";
import {integrationActions} from "../../../store/actions/integrationAction";
import {connect} from "react-redux";
import {useCallback, useEffect, useState} from "react";
import Spinner from "../../spinner/Spinner";
import {Check} from "@mui/icons-material";
import {useFieldArray, useForm} from "react-hook-form";
import {alertActions} from "../../../store/actions/alertActions";
import {CustomListItem, CustomListItemIcon, CustomListItemText} from "./RevContentIntegration";

function NewsBreakIntegration(props) {
    const theme = useTheme();
    const {integration, resetIntegration, removeIntegrationEvent, create, update, find, alert, getAdAccounts} = props;

    const steps = ["Connect", 'Integrate Account', 'Conversion API'];

    const integrationId = parseInt(props?.match?.params?.id);

    const [open, setOpen] = useState(true);
    const [activeStep, setActiveStep] = useState(1);
    const [removeEventIndex, setRemoveEventIndex] = useState(null);
    const [step1Data, setStep1Data] = useState(null);
    const [step2Data, setStep2Data] = useState(null);

    const {register, handleSubmit, control, setValue, watch, getValues, formState: {errors}} = useForm();
    const {fields, remove, append} = useFieldArray({
        control,
        name: "events"
    });

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }

        resetIntegration();
        setOpen(false);
        props.history.push({pathname: "/settings/integrations", state: {from: props.location}});
    }, [resetIntegration, props.history, props.location]);

    const handleOnSubmitStep1 = (data) => {
        setStep1Data(data);
        setActiveStep(2);
    };

    const handleOnIntegrateAdAccount = (data) => {
        setStep2Data(data);
    };

    const handleOnSubmitStep2 = () => {
        setActiveStep(3);
    };

    const handleOnSubmit = (data) => {
        if (integrationId > 0) {
            update({
                integrationId: integrationId,
                name: step1Data?.name,
                accessToken: step1Data?.accessToken,
                organizationId: step1Data?.organizationId,
                enableConversionAPI: data?.enableConversionAPI,
                events: data?.events,
                adAccountId: step2Data?.adAccountId,
                adAccountName: step2Data?.adAccountName,
                currency: step2Data?.currency,
            });
        } else {
            create({
                name: step1Data?.name,
                accessToken: step1Data?.accessToken,
                organizationId: step1Data?.organizationId,
                enableConversionAPI: data?.enableConversionAPI,
                events: data?.events,
                adAccountId: step2Data?.adAccountId,
                adAccountName: step2Data?.adAccountName,
                currency: step2Data?.currency,
            });
        }
    };

    const appendNewEvent = () => {
        append({
            eventId: 0,
            name: "",
            includePayout: false
        });
    };

    const handleDeleteIntegrationEvent = useCallback(({eventId, index}) => {
        if (eventId === 0) {
            remove(index); // from list
            return false;
        }
        setRemoveEventIndex({eventId, index}); // set event to remove from list and do API req.
    }, [remove, setRemoveEventIndex]);

    useEffect(() => {
        if (removeEventIndex !== null) {
            remove(removeEventIndex?.index); // from list
            removeIntegrationEvent({integrationId, eventId: removeEventIndex?.eventId}); // API req
        }
    }, [integrationId, remove, removeEventIndex, removeIntegrationEvent]);

    useEffect(() => {
        if (integrationId > 0) {
            find(integrationId);
        }
    }, [find, integrationId]);

    useEffect(() => {
        if (activeStep === 1 && integrationId > 0 && integration?.currentIntegration !== null) {
            if (integration?.currentIntegration?.id === integrationId) {
                setValue("name", integration?.currentIntegration?.networkName);
                for (const cred of integration?.currentIntegration?.integrationCredentials) {
                    if (cred?.keyName === "nb_access_token") {
                        setValue("accessToken", cred?.keyValue);
                    }
                    if (cred?.keyName === "nb_organization_id") {
                        setValue("organizationId", cred?.keyValue);
                    }
                    if (cred?.keyName === "nb_account_currency") {
                        setValue("currency", cred?.keyValue);
                    }
                }
            }
        }
    }, [activeStep, setValue, integration?.currentIntegration, integrationId]);

    // Set step 1 form data.
    useEffect(() => {
        if (activeStep === 1 && step1Data !== null) {
            setValue("name", step1Data?.name);
            setValue("accessToken", step1Data?.accessToken);
            setValue("organizationId", step1Data?.organizationId);
        }
    }, [activeStep, setValue, step1Data]);

    // Load NewsBreak ad accounts if step 2.
    useEffect(() => {
        if (activeStep === 2 && integration?.adAccounts === null) {
            getAdAccounts({
                accessToken: step1Data?.accessToken,
                organizationId: step1Data?.organizationId,
                integrationId: integrationId
            });
        }
    }, [activeStep, getAdAccounts, step1Data?.accessToken, step1Data?.organizationId, integration?.adAccounts, integrationId]);

    // If integrated then set step 2 data.
    useEffect(() => {
        if (activeStep === 2 && integration?.adAccounts !== null) {
            integration?.adAccounts.forEach((item) => {
                if (item?.integrated) {
                    setStep2Data({
                        adAccountId: item?.adAccountId,
                        adAccountName: item?.adAccountName,
                        currency: item?.currency
                    });
                }
            });
        }
    }, [activeStep, integration?.adAccounts])

    useEffect(() => {
        if (activeStep === 3 && integration?.currentIntegration !== null && getValues("events")?.length === 0) {
            for (const cred of integration?.currentIntegration?.integrationCredentials) {
                if (cred?.keyName === "nb_enable_conversion_api") {
                    if (cred?.keyValue === "true") {
                        setValue("enableConversionAPI", true);
                    } else {
                        setValue("enableConversionAPI", false);
                    }
                }
            }
            let events = [];
            integration?.currentIntegration?.integrationEvents?.forEach((event) => {
                events.push({
                    eventId: event?.id,
                    name: event?.name,
                    includePayout: event?.includePayout
                });
            });
            setValue("events", events);
        }
    }, [activeStep, setValue, integration?.currentIntegration, getValues]);

    useEffect(() => {
        if (alert?.successMsg !== "") {
            handleClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alert?.successMsg, handleClose]);

    return (
        <Dialog open={open}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                maxWidth={"sm"}
                aria-labelledby="newsbreak-account-integration"
        >
            <IntegrationHeader name="NewsBreak" logo="/assets/images/traffic-sources/newsbreak-white.svg" id="newsbreak-account-integration"/>
            <DialogContent>
                <Stepper
                    alternativeLabel={fullScreen}
                    activeStep={activeStep - 1}
                    connector={
                        <StepConnector
                            sx={{"& .MuiStepConnector-line ": {borderColor: theme.palette.background.tableBorder}}}
                        />}
                    sx={{
                        margin: "0 auto",
                        width: "100%",
                        maxWidth: 800,
                        padding: "0px 24px 24px 24px"
                    }}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 1 &&
                    <>
                        <p>
                            Please check our integration
                            guide <Link target="_blank" underline="hover" rel="noreferrer"
                                        href="https://skro.eu/docs/integrations/newsbreak-and-skro">here</Link>.
                        </p>

                        {integration?.loading?.integration && <Spinner overComponentBox={true}/>}
                        <form onSubmit={handleSubmit(handleOnSubmitStep1)} autoComplete={"off"}>
                            <Box display="flex" flexWrap={"wrap"}>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.name}
                                        variant="outlined"
                                        margin="dense"
                                        id="name"
                                        label="Integration Name*"
                                        {...register("name", {required: true, maxLength: 255, minLength: 2})}
                                        name="name"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your integration name"
                                        fullWidth={true}
                                        defaultValue="NewsBreak"
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.accessToken}
                                        variant="outlined"
                                        margin="dense"
                                        id="accessToken"
                                        label="Access Token*"
                                        {...register("accessToken", {required: true, maxLength: 250, minLength: 2})}
                                        name="accessToken"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your Access Token"
                                        fullWidth={true}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.organizationId}
                                        variant="outlined"
                                        margin="dense"
                                        id="organizationId"
                                        label="Organization ID*"
                                        {...register("organizationId", {required: true, maxLength: 50, minLength: 2})}
                                        name="organizationId"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your Organization ID"
                                        fullWidth={true}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                        </form>

                        <List>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Check the status of your campaigns, ad sets and ads.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Change daily budget and bid for ad sets.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Pause or resume campaigns, ad sets and ads.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Check delivery status of campaigns, ad sets and ads.
                                </CustomListItemText>
                            </CustomListItem>
                            <CustomListItem>
                                <CustomListItemIcon>
                                    <Check color={"primary"}/>
                                </CustomListItemIcon>
                                <CustomListItemText>
                                    Sync cost on ad level every 30 min.
                                </CustomListItemText>
                            </CustomListItem>
                        </List>
                    </>
                }

                {activeStep === 2 &&
                    <Box mb={2}>
                        <Box mb={2} style={{fontWeight: 600}}>
                            <Alert variant="filled" severity="info">
                                Choose the NewsBreak ad account you want to integrate. If you have multiple ad accounts
                                then you need to integrate them separately.
                            </Alert>
                        </Box>
                        <Box sx={{
                            fontWeight: 500,
                            fontSize: "14px",
                            paddingTop: 1,
                            paddingBottom: "4px"
                        }}>
                            NewsBreak Ad Accounts
                        </Box>
                        {
                            integration?.loading?.adAccounts ? (
                                <Spinner overComponentBox={true}/>
                            ) : (
                                <IntegrationAdAccountList provider="NewsBreak">
                                    {integration?.adAccounts?.map((item) => {


                                        return (
                                            <IntegrationAdAccountRow key={item?.adAccountId} name={item?.adAccountName}
                                                accountId={item?.adAccountId} integrated={item?.adAccountId === step2Data?.adAccountId}
                                                onIntegrate={() => handleOnIntegrateAdAccount({
                                                            adAccountId: item?.adAccountId,
                                                            adAccountName: item?.adAccountName,
                                                            currency: item?.currency
                                                        })}/>
                                        );
                                    })}
                                </IntegrationAdAccountList>
                            )
                        }
                    </Box>
                }

                {activeStep === 3 &&
                    <Box sx={{position: "relative"}}>
                        {(integration?.loading?.conversionApi || integration?.loading?.integration) &&
                            <Spinner overComponentBox={true}/>}
                        <IntegrationConversionSettings provider="NewsBreak" control={control} register={register} errors={errors}
                            enabled={watch("enableConversionAPI")} eventFields={fields}
                            onAddEvent={appendNewEvent} onDeleteEvent={handleDeleteIntegrationEvent}/>
                    </Box>
                }
            </DialogContent>
            <DialogActions>
                <Box display="flex" justifyContent="flex-end">
                    <Box mr={3}>
                        <Button onClick={handleClose} color="inherit" variant="text">
                            Close
                        </Button>
                    </Box>
                    {activeStep === 1 &&
                        <Box>
                            <Button onClick={handleSubmit(handleOnSubmitStep1)} color="primary" variant="contained">
                                Next
                            </Button>
                        </Box>
                    }
                    {activeStep === 2 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(1)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmitStep2)} color="primary" variant="contained">
                                    Next
                                </Button>
                            </Box>
                        </>
                    }
                    {activeStep === 3 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(2)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmit)} color="primary" variant="contained">
                                    Save & Close
                                </Button>
                            </Box>
                        </>
                    }
                </Box>
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = (state) => {
    return {
        integration: state.integration,
        alert: state.alert
    };
};

const mapDispatchToProps = {
    resetIntegration: integrationActions.reset,
    find: integrationActions.findIntegration,
    getAdAccounts: integrationActions.getNewsBreakAdAccounts,
    create: integrationActions.createNewsBreakIntegration,
    update: integrationActions.updateNewsBreakIntegration,
    error: alertActions.error,
    removeIntegrationEvent: integrationActions.removeIntegrationEventFromCurrentIntegration
};

export default connect(mapStateToProps, mapDispatchToProps)(NewsBreakIntegration);
